import React from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Calendar, Building2 } from "lucide-react";
import Button from "../components/Button";

export default function Contact() {
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    company: "",
    role: "",
    message: "",
  });
  const [submitting, setSubmitting] = React.useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      toast.success("Request received. Our team will reach out within 24 hours.");
      setForm({ name: "", email: "", company: "", role: "", message: "" });
      setSubmitting(false);
    }, 700);
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="page-contact"
    >
      {/* Hero header */}
      <section className="relative pt-[140px] md:pt-[180px] pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-purple via-brand-purpleMid to-brand-purpleLight" />
          <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-brand-orange/30 blur-3xl" />
          <div className="noise-overlay opacity-60" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <div className="font-display text-[11px] uppercase tracking-[0.28em] text-white/90 font-bold">
            Contact us
          </div>
          <h1
            className="mt-5 font-display text-white text-[46px] md:text-[74px] leading-[0.98] tracking-tight max-w-4xl text-balance"
            style={{ fontWeight: 800 }}
          >
            Talk to the team{" "}
            <span className="grad-text">building the future of hospitality.</span>
          </h1>
          <p className="mt-6 text-white/85 text-[17px] md:text-[19px] leading-relaxed max-w-2xl">
            Two offices. One mission. Write to us at{" "}
            <a
              href="mailto:info@inplass.com"
              className="underline underline-offset-4 decoration-brand-orange"
              data-testid="contact-email-inline"
            >
              info@inplass.com
            </a>{" "}
            or drop by.
          </p>
        </div>
      </section>

      {/* Offices */}
      <section className="relative py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <OfficeCard
            testId="office-mena"
            tag="MENA · Regional office"
            company="iNPLASS iNFOTECH"
            lines={[
              "1/108, Bu Shaqer Tower B",
              "Garhoud, Dubai, UAE",
            ]}
            phone="+971 4 326 000 1"
          />
          <OfficeCard
            testId="office-corporate"
            tag="Corporate office"
            company="iNPLASS iNFOTECH"
            lines={[
              "8 The Green STE R",
              "Dover DE 19901, Delaware, USA",
            ]}
            phone=""
          />
        </div>
      </section>

      <section className="relative pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: contact info + map */}
          <div className="lg:col-span-5 space-y-4">
            <ContactCard
              icon={Mail}
              title="Email"
              lines={[
                "info@inplass.com",
                "Sales, support and partnerships — one inbox.",
              ]}
              testId="contact-email-card"
            />
            <ContactCard
              icon={Phone}
              title="MENA phone"
              lines={["+971 4 326 000 1", "Sun–Thu · 9am → 6pm GST"]}
              testId="contact-phone-card"
            />
            <ContactCard
              icon={Building2}
              title="Offices"
              lines={["Dubai, UAE (Regional)", "Delaware, USA (Corporate)"]}
              testId="contact-offices-card"
            />

            {/* Map placeholder */}
            <div className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="h-56 relative brand-panel">
                <iframe
                  title="Dubai office map"
                  src="https://www.google.com/maps?q=Bu+Shaqer+Tower+Garhoud+Dubai&output=embed"
                  className="w-full h-full border-0 opacity-90"
                  data-testid="google-map-embed"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* Right: form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative rounded-2xl brand-panel p-8 md:p-12"
            data-testid="contact-form"
          >
            <div className="flex items-center gap-2 text-white/80">
              <Calendar size={16} className="text-white" />
              <span className="font-display text-[11px] uppercase tracking-[0.28em] font-bold">
                Book a demo · we&apos;ll schedule via Calendly
              </span>
            </div>
            <h2
              className="mt-3 font-display text-white text-[30px] md:text-[40px] leading-[1.05] tracking-tight"
              style={{ fontWeight: 800 }}
            >
              Tell us about your property.
            </h2>
            <p className="mt-2 text-white/70 text-[14px]">
              We&apos;ll get back within one business day with a tailored walkthrough.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="input"
                  placeholder="Amelia Park"
                  data-testid="contact-input-name"
                />
              </Field>
              <Field label="Work email" required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="input"
                  placeholder="amelia@hotel.com"
                  data-testid="contact-input-email"
                />
              </Field>
              <Field label="Company">
                <input
                  value={form.company}
                  onChange={update("company")}
                  className="input"
                  placeholder="Eclipse Hospitality"
                  data-testid="contact-input-company"
                />
              </Field>
              <Field label="Role">
                <input
                  value={form.role}
                  onChange={update("role")}
                  className="input"
                  placeholder="General Manager"
                  data-testid="contact-input-role"
                />
              </Field>
              <Field label="How can we help?" className="md:col-span-2">
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  className="input min-h-[120px] resize-y"
                  placeholder="A few words about your properties, team size, and what you want to solve."
                  data-testid="contact-input-message"
                />
              </Field>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <div className="text-white/60 text-[12px] max-w-sm">
                By submitting, you agree to our terms and privacy policy.
              </div>
              <Button
                type="submit"
                onClick={() => {}}
                variant="primary"
                testId="contact-submit-btn"
              >
                {submitting ? "Sending…" : "Request demo"}
              </Button>
            </div>

            <style>{`
              .input {
                width: 100%;
                background: rgba(255,255,255,0.08);
                border: 1px solid rgba(255,255,255,0.14);
                border-radius: 10px;
                padding: 12px 14px;
                color: #fff;
                font-size: 14px;
                font-family: 'Nunito Sans', Avenir, Tahoma, sans-serif;
                outline: none;
                transition: border-color 200ms ease, background 200ms ease;
              }
              .input::placeholder { color: rgba(255,255,255,0.45); }
              .input:focus {
                border-color: rgba(255,255,255,0.6);
                background: rgba(255,255,255,0.12);
              }
            `}</style>
          </motion.form>
        </div>
      </section>
    </motion.main>
  );
}

function OfficeCard({ tag, company, lines, phone, testId }) {
  return (
    <div
      className="relative rounded-2xl brand-panel p-8 overflow-hidden group"
      data-testid={testId}
    >
      <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-brand-orange/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="relative">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/15 grid place-items-center text-white">
            <MapPin size={18} />
          </div>
          <div>
            <div className="font-display text-[10px] uppercase tracking-[0.28em] text-brand-orange font-bold">
              {tag}
            </div>
            <div
              className="font-display text-white text-[22px] leading-tight"
              style={{ fontWeight: 800 }}
            >
              {company}
            </div>
          </div>
        </div>
        <div className="mt-5 text-white/85 leading-relaxed">
          {lines.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
        {phone && (
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1.5">
            <Phone size={12} className="text-white" />
            <span className="text-white text-[13px] font-semibold">{phone}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactCard({ icon: Icon, title, lines, testId }) {
  return (
    <div
      className="rounded-2xl border border-white/10 bg-brand-surface/70 p-6 flex items-start gap-4 hover:border-brand-purpleLight/50 transition-colors"
      data-testid={testId}
    >
      <div className="w-10 h-10 rounded-lg bg-brand-purple/40 grid place-items-center text-white">
        <Icon size={18} />
      </div>
      <div>
        <div className="font-display text-[10px] uppercase tracking-[0.28em] text-white/60 font-bold">
          {title}
        </div>
        <div className="mt-1 text-white text-[15px] font-semibold">{lines[0]}</div>
        {lines[1] && (
          <div className="mt-1 text-white/60 text-[13px]">{lines[1]}</div>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="font-display text-[10px] uppercase tracking-[0.28em] text-white/70 font-bold mb-2">
        {label} {required && <span className="text-brand-orange">*</span>}
      </div>
      {children}
    </label>
  );
}
